import { ApiError } from "@/shared/models/ApiError";
import { apiConfig } from "@/shared/config/apiConfig";
import {
  resolveAuthHeaderValue,
  attemptTokenRenew,
  invokeAuthInvalidation,
  type AuthInvalidationReason,
} from "@/apis/http/authHeaders";
import { applyAuthorizationHeader } from "@/apis/http/authorizationHeader";
import { isDemoMode, DEMO_MODE_HEADER } from "@/shared/config/demoMode";

interface ApiFetchOptions extends RequestInit {
  /** When true, 404 responses return the raw Response instead of throwing. */
  allow404?: boolean;
  /** When true, do not apply the default API timeout. Useful for large downloads. */
  disableTimeout?: boolean;
  /**
   * When true, skip automatic sign-out side-effects for 401/403 handling.
   * Useful for auth probe flows (for example the root-route login gate).
   */
  suppressAuthRedirect?: boolean;
}

let fallbackRedirecting = false;
const accountDisabledMessage = "Account disabled/contact admin";

function isAbsoluteUrl(value: string): boolean {
  return /^[a-z][a-z\d+.-]*:/i.test(value);
}

function resolveRequestUrl(url: string): string {
  if (isAbsoluteUrl(url)) {
    return url;
  }

  return new URL(url, `${apiConfig.baseUrl}/`).toString();
}

function getErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") {
    return null;
  }

  const record = body as Record<string, unknown>;
  const message = record.Error ?? record.error ?? record.message;
  return typeof message === "string" ? message : null;
}

function signOutReason(status: number, body: unknown): AuthInvalidationReason | null {
  if (status === 401) {
    return "sessionExpired";
  }

  if (status === 403 && getErrorMessage(body) === accountDisabledMessage) {
    return "accessRevoked";
  }

  return null;
}

function handleAuthInvalidation(reason: AuthInvalidationReason): void {
  if (globalThis.window === undefined) {
    return;
  }

  // The auth layer registers a handler that clears the session and returns to
  // the login route; it is one-shot per session (see invokeAuthInvalidation).
  // If it isn't registered yet, fall back to a single plain redirect.
  if (invokeAuthInvalidation(reason)) {
    return;
  }

  if (fallbackRedirecting) {
    return;
  }

  fallbackRedirecting = true;
  globalThis.window.location.assign("/");
}

/**
 * Issues the actual fetch: builds headers (default Content-Type + Authorization),
 * applies the request timeout, and normalizes network/timeout failures into ApiError.
 *
 * When `authHeaderOverride` is supplied it is used verbatim (the 401 retry passes the
 * freshly renewed bearer token); otherwise the current token is resolved on demand.
 */
async function performFetch(
  requestUrl: string,
  fetchOptions: RequestInit,
  disableTimeout: boolean | undefined,
  authHeaderOverride?: string,
): Promise<Response> {
  const headers = new Headers(fetchOptions.headers);
  if (!headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Signal demo mode to the backend, which serves synthetic data (SystemAdmin only).
  if (isDemoMode()) {
    headers.set(DEMO_MODE_HEADER, "true");
  }

  if (authHeaderOverride) {
    // The 401 retry must always carry the freshly renewed token, even if the
    // caller supplied an explicit Authorization header (which the resolver path
    // below deliberately preserves) — otherwise the replay would repeat the
    // stale credentials and force a spurious sign-out.
    headers.set("Authorization", authHeaderOverride);
  } else {
    applyAuthorizationHeader(headers, await resolveAuthHeaderValue());
  }

  // Abort the request if the backend does not respond within the configured
  // timeout, so a hung connection cannot leave a query pending indefinitely.
  const timeoutSignal =
    !disableTimeout && apiConfig.timeoutMs > 0 ? AbortSignal.timeout(apiConfig.timeoutMs) : null;
  const signals = [fetchOptions.signal, timeoutSignal].filter((signal): signal is AbortSignal =>
    Boolean(signal),
  );
  const combinedSignal = signals.length > 0 ? AbortSignal.any(signals) : undefined;

  try {
    return await fetch(requestUrl, {
      ...fetchOptions,
      headers,
      signal: combinedSignal,
    });
  } catch (err) {
    if (timeoutSignal?.aborted) {
      console.warn(`[apiFetch] Request timed out after ${apiConfig.timeoutMs}ms: ${requestUrl}`);
      throw new ApiError(0, "Request timed out", null);
    }
    console.warn(`[apiFetch] Network error: ${requestUrl}`, err);
    throw err;
  }
}

/**
 * Thin wrapper around fetch() that centralizes:
 * - Default Content-Type header (application/json)
 * - Error normalization into ApiError
 * - 401 recovery: silently renew the token and replay the request once before
 *   falling back to sign-out + redirect to login
 * - auth invalidation interception (triggers sign-out + redirect to login)
 * - Centralized error logging (console.warn now, swap for toast later)
 *
 * Auth is handled client-side via the shared auth header resolver.
 */
export async function apiFetch(url: string, options?: ApiFetchOptions): Promise<Response> {
  const { allow404, disableTimeout, suppressAuthRedirect, ...fetchOptions } = options ?? {};
  const requestUrl = resolveRequestUrl(url);

  let res = await performFetch(requestUrl, fetchOptions, disableTimeout);

  // A 401 usually means the in-flight access token just expired. Ask the auth
  // layer for a silent renewal and replay the request once with the fresh token;
  // only if renewal fails (or the replay is still 401) do we fall through to
  // sign-out below. Skipped for auth-probe flows via suppressAuthRedirect.
  // NOTE: the replay reuses fetchOptions.body, which is safe because every caller
  // passes a re-readable body (JSON strings). If one-shot bodies (streams) are
  // ever introduced, they must be recreated per attempt.
  if (res.status === 401 && !suppressAuthRedirect) {
    const newToken = await attemptTokenRenew();
    if (newToken) {
      res = await performFetch(requestUrl, fetchOptions, disableTimeout, `Bearer ${newToken}`);
    }
  }

  if (allow404 && res.status === 404) {
    return res;
  }

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = null;
    }

    // A 401 that survived the renewal retry means the session is genuinely gone.
    // If Tower revoked membership while the browser still has a valid Entra
    // session, the backend returns a specific 403; treat it like a dead session
    // (no renewal helps). Ordinary role/authorization 403s still surface normally.
    const reason = suppressAuthRedirect ? null : signOutReason(res.status, body);
    if (reason) {
      handleAuthInvalidation(reason);
    }

    const error = new ApiError(res.status, res.statusText, body);
    console.warn(`[apiFetch] ${error.message}`, { url: requestUrl, body });
    throw error;
  }

  return res;
}
